import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './inbox.css';
import toast from 'react-hot-toast';
import {
  getInboxConversations, getInboxMessages, getInboxAgents,
  inboxAssign, inboxUnassign, inboxReply, inboxAddNote, inboxReact, inboxResolve, inboxHandback,
  inboxCreateAgent, inboxUpdateAgent, inboxDeleteAgent, inboxFetchMedia,
  inboxClearMessages,
  getInboxCannedResponses, inboxCreateCanned, inboxUpdateCanned, inboxDeleteCanned,
  getInboxLabels, inboxCreateLabel, inboxUpdateLabel, inboxDeleteLabel, inboxSetLabels,
  getInboxContact, getInboxContactHistory, inboxSetContactField,
  searchInboxMessages,
  getInboxReports,
} from '../../../api/client';
import CannedResponseDropdown, { matchCannedTrigger, filterCannedResponses } from '../../../components/common/CannedResponsePicker';
import { parseLabelIds, labelsForConversation, LabelChip, LabelPickerPopover, ColorSwatch } from '../../../components/common/LabelPicker';
import ContactSidebar from '../../../components/common/ContactSidebar';

// ── Utility ───────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400)return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
}

function modeBadge(mode) {
  const map = {
    bot     : { label: 'Bot',     cls: 'badge-bot'     },
    agent   : { label: 'Agent',   cls: 'badge-agent'   },
    waiting : { label: 'Waiting', cls: 'badge-waiting' },
  };
  const m = map[mode] ?? { label: mode, cls: '' };
  return <span className={`inbox-badge ${m.cls}`}>{m.label}</span>;
}

// Shared confirm dialog -- replaces native window.confirm() everywhere in
// this tab with something that matches the app's own modal styling.
// Usage: setConfirm({ title, message, confirmLabel, danger, onConfirm })
function ConfirmModal({ title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box confirm-modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onCancel} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="modal-body">
          <p className="confirm-modal-msg">{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const AVATAR_PALETTE = ['#7c3aed', '#0ea5a4', '#db2777', '#2563eb', '#ea580c', '#16a34a', '#9333ea', '#0891b2'];
// "2348031234412" → "+234 803 123 4412" (WhatsApp-style); anything else is returned as-is
function prettyPhone(id = '') {
  if (String(id).startsWith('web:')) return 'Website chat';
  const d = String(id).replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('234')) return `+234 ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
  if (d.length >= 10 && d.length <= 15 && d === String(id)) return `+${d}`;
  return id;
}

function avatarColor(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

// ── Main InboxTab ─────────────────────────────────────────────
export default function InboxTab({ bId }) {
  const [conversations, setConversations]   = useState([]);
  const [selected, setSelected]             = useState(null); // chatId
  const [messages, setMessages]             = useState([]);
  const [agents, setAgents]                 = useState([]);
  const [waitingCount, setWaitingCount]     = useState(0);
  const [loading, setLoading]               = useState(true);
  const [msgLoading, setMsgLoading]         = useState(false);
  const [replyText, setReplyText]           = useState('');
  const [replyingTo, setReplyingTo]         = useState(null); // { waId, text, senderLabel } or null
  const [replyMode, setReplyMode]           = useState('reply'); // 'reply' | 'note'
  const [sending, setSending]               = useState(false);
  const [filterMode, setFilterMode]         = useState('');   // ''=all
  const [labelFilter, setLabelFilter]       = useState(0);    // lId, 0=no filter
  const [convSearch, setConvSearch]         = useState('');
  const [msgSearchResults, setMsgSearchResults] = useState([]);
  const [msgSearchLoading, setMsgSearchLoading] = useState(false);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  const [activeTab, setActiveTab]           = useState('conversations'); // conversations | agents | canned | labels
  const [agentModal, setAgentModal]         = useState(null); // null | 'create' | agent obj
  const [cannedResponses, setCannedResponses] = useState([]);
  const [cannedModal, setCannedModal]       = useState(null); // null | 'create' | canned obj
  const [cannedOpen, setCannedOpen]         = useState(false);   // "/" dropdown visible in reply box
  const [cannedActiveIndex, setCannedActiveIndex] = useState(0);
  const [labels, setLabels]                 = useState([]);
  const [labelModal, setLabelModal]         = useState(null); // null | 'create' | label obj
  const [labelPickerOpen, setLabelPickerOpen] = useState(false); // chat-header "+ Label" popover
  const [reportsRange, setReportsRange]     = useState('week'); // today | week | all
  const [reportsData, setReportsData]       = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [confirm, setConfirm]               = useState(null); // null | { title, message, confirmLabel, danger, onConfirm }
  const [mobileView, setMobileView]         = useState('list'); // list | chat | contact (mobile only)
  const [isMobile, setIsMobile]             = useState(() => window.innerWidth <= 768);
  const [contactOpen, setContactOpen]       = useState(() => window.innerWidth >= 1280); // desktop-only collapse toggle; starts closed on smaller screens
  const [contact, setContact]               = useState(null);
  const [contactFields, setContactFields]   = useState({});
  const [contactLoading, setContactLoading] = useState(false);
  const [contactEvents, setContactEvents]   = useState([]);
  const [eventsLoading, setEventsLoading]   = useState(false);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [newMsgCount, setNewMsgCount]       = useState(0);       // unseen messages while scrolled up
  const [hasMoreOlder, setHasMoreOlder]     = useState(false);   // more history above what's loaded
  const [loadingOlder, setLoadingOlder]     = useState(false);
  const [convLimit, setConvLimit]           = useState(50);      // "Load more" bumps this up by 50
  const [hasMoreConvs, setHasMoreConvs]     = useState(false);
  const [loadingMoreConvs, setLoadingMoreConvs] = useState(false);
  const messagesEndRef                      = useRef(null);
  const messagesContainerRef                = useRef(null);
  const isNearBottomRef                     = useRef(true);   // tracks if user is scrolled to bottom
  const justSwitchedRef                     = useRef(false);  // true right after picking a new conversation
  const prevMsgCountRef                     = useRef(0);
  const messagesRef                         = useRef([]);     // always-current mirror of `messages`, for the poll/load-older closures below
  const isLoadingOlderRef                   = useRef(false);  // guards against duplicate "load older" triggers while scrolling
  const pollRef                             = useRef(null);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Current selected conversation object
  const selectedConv = conversations.find(c => c.cChatId === selected) ?? null;

  // Conversations narrowed by the search box
  const filteredConversations = convSearch.trim()
    ? conversations.filter(c => {
        const q = convSearch.trim().toLowerCase();
        return (c.cName || '').toLowerCase().includes(q) || (c.cChatId || '').toLowerCase().includes(q);
      })
    : conversations;

  // ── Full-text message search ─────────────────────────────────
  // convSearch above only ever matches contact name/phone against the
  // conversations already loaded client-side. This debounces a real
  // server-side search across message CONTENT so "did anyone ever say
  // X" works even for a conversation that isn't currently in view.
  useEffect(() => {
    const q = convSearch.trim();
    if (q.length < 2) { setMsgSearchResults([]); setMsgSearchLoading(false); return; }
    setMsgSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchInboxMessages(bId, q);
        setMsgSearchResults(res.data?.results ?? []);
      } catch { setMsgSearchResults([]); }
      setMsgSearchLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [convSearch, bId]);

  // ── Load conversations ──────────────────────────────────────
  //  Uses convLimit (bumped by 50 each time "Load more" is clicked)
  //  rather than true offset pagination — simplest way to keep the
  //  5s silent poll's full-replace behaviour correct without having
  //  to merge/de-dupe pages by hand.
  const loadConversations = useCallback(async (silent = false, limitOverride = null) => {
    if (!silent) setLoading(true);
    try {
      const res = await getInboxConversations(bId, {
        mode: filterMode, status: 'open', limit: limitOverride ?? convLimit,
        ...(labelFilter ? { label: labelFilter } : {}),
      });
      const d = res.data;
      setConversations(d.conversations ?? []);
      setWaitingCount(d.waiting_count ?? 0);
      setHasMoreConvs(!!d.has_more);
    } catch { if (!silent) toast.error('Failed to load conversations'); }
    if (!silent) setLoading(false);
  }, [bId, filterMode, labelFilter, convLimit]);

  async function loadMoreConversations() {
    setLoadingMoreConvs(true);
    const nextLimit = convLimit + 50;
    await loadConversations(true, nextLimit);
    setConvLimit(nextLimit);
    setLoadingMoreConvs(false);
  }

  // Switching filters starts back at the first page
  useEffect(() => { setConvLimit(50); }, [filterMode, labelFilter]);

  // ── Load messages for selected conversation (initial load / full reset) ──
  //  Always anchors to the NEWEST end of the conversation — see the
  //  backend fix notes in api/bots/inbox/index.php for why that matters.
  const loadMessages = useCallback(async (chatId, silent = false, aroundId = null) => {
    if (!silent) setMsgLoading(true);
    try {
      const res = await getInboxMessages(bId, chatId, aroundId ? { around: aroundId, limit: 50 } : { limit: 50 });
      setMessages(res.data?.messages ?? []);
      setHasMoreOlder(!!res.data?.has_more);
    } catch { /* silent */ }
    if (!silent) setMsgLoading(false);
  }, [bId]);

  // ── Load older messages — triggered by scrolling near the top,
  //    or by the "Load earlier messages" button. Prepends to the
  //    array and restores scroll position so the view doesn't jump. ──
  const loadOlderMessages = useCallback(async (chatId) => {
    const current = messagesRef.current;
    if (isLoadingOlderRef.current || current.length === 0) return;
    const oldestId = current[0]?.mId;
    if (oldestId == null) return;

    isLoadingOlderRef.current = true;
    setLoadingOlder(true);
    const el = messagesContainerRef.current;
    const prevScrollHeight = el ? el.scrollHeight : 0;

    try {
      const res = await getInboxMessages(bId, chatId, { before: oldestId, limit: 50 });
      const older = res.data?.messages ?? [];
      setHasMoreOlder(!!res.data?.has_more);
      if (older.length > 0) {
        setMessages(prev => [...older, ...prev]);
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
        });
      }
    } catch { /* silent */ }
    setLoadingOlder(false);
    isLoadingOlderRef.current = false;
  }, [bId]);

  // ── Poll: fetch only what's new since the last message we have,
  //    and append rather than replace — so a long conversation never
  //    silently caps out, and any older history loaded via "load
  //    older" above stays put instead of getting wiped every 5s. ──
  const pollNewMessages = useCallback(async (chatId) => {
    const current = messagesRef.current;
    const newestId = current.length ? current[current.length - 1].mId : null;
    if (newestId == null) return;
    try {
      const res = await getInboxMessages(bId, chatId, { after: newestId });
      const fresh = res.data?.messages ?? [];
      if (fresh.length > 0) setMessages(prev => [...prev, ...fresh]);
    } catch { /* silent */ }
  }, [bId]);

  // ── Load agents ─────────────────────────────────────────────
  const loadAgents = useCallback(async () => {
    try {
      const res = await getInboxAgents(bId);
      setAgents(res.data?.agents ?? []);
    } catch { /* silent */ }
  }, [bId]);

  // ── Load canned responses ───────────────────────────────────
  const loadCannedResponses = useCallback(async () => {
    try {
      const res = await getInboxCannedResponses(bId);
      setCannedResponses(res.data?.canned_responses ?? []);
    } catch { /* silent */ }
  }, [bId]);

  // ── Load labels ──────────────────────────────────────────────
  const loadLabels = useCallback(async () => {
    try {
      const res = await getInboxLabels(bId);
      setLabels(res.data?.labels ?? []);
    } catch { /* silent */ }
  }, [bId]);

  const labelsById = labels.reduce((acc, l) => { acc[l.lId] = l; return acc; }, {});

  const loadReports = useCallback(async (range = reportsRange) => {
    setReportsLoading(true);
    try {
      const res = await getInboxReports(bId, range);
      setReportsData(res.data ?? null);
    } catch { toast.error('Failed to load reports'); }
    setReportsLoading(false);
  }, [bId, reportsRange]);

  // ── Initial load ────────────────────────────────────────────
  useEffect(() => {
    loadConversations();
    loadAgents();
    loadCannedResponses();
    loadLabels();
  }, [loadConversations, loadAgents, loadCannedResponses, loadLabels]);

  // ── Poll every 5s ───────────────────────────────────────────
  useEffect(() => {
    pollRef.current = setInterval(() => {
      loadConversations(true);
      if (selected) pollNewMessages(selected);
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [loadConversations, pollNewMessages, selected]);

  // ── Scroll behaviour, WhatsApp-style ───────────────────────────
  // - If you're at the bottom, new messages scroll you down automatically.
  // - If you've scrolled up to read history, we leave you alone and
  //   just count what came in, surfaced via a "↓ N new messages" pill.
  useEffect(() => {
    if (messages.length === 0) return; // nothing to scroll to yet
    const added = messages.length - prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;

    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: justSwitchedRef.current ? 'auto' : 'smooth' });
      justSwitchedRef.current = false;
      setNewMsgCount(0);
    } else if (added > 0) {
      setNewMsgCount(c => c + added);
    }
  }, [messages]);

  // ── Track whether user is scrolled near the bottom, and auto-load
  //    older history once they scroll near the top (mirrors how
  //    WhatsApp Web itself lazy-loads further back as you scroll up,
  //    so a long conversation never just "runs out" of visible history) ──
  function handleMessagesScroll() {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nowNearBottom = distanceFromBottom < 80;
    isNearBottomRef.current = nowNearBottom;
    if (nowNearBottom) setNewMsgCount(0);

    if (el.scrollTop < 100 && hasMoreOlder && !isLoadingOlderRef.current && selected) {
      loadOlderMessages(selected);
    }
  }

  function jumpToBottom() {
    isNearBottomRef.current = true;
    setNewMsgCount(0);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  // ── Select conversation ─────────────────────────────────────
  async function selectConversation(chatId) {
    setSelected(chatId);
    setMessages([]);
    setHasMoreOlder(false);
    isNearBottomRef.current = true;   // always land on the bottom for a freshly opened chat
    justSwitchedRef.current = true;
    prevMsgCountRef.current = 0;
    setNewMsgCount(0);
    setMobileView('chat');            // on mobile, switch to the chat view
    setLabelPickerOpen(false);
    await loadMessages(chatId);
    loadContact(chatId);
  }

  // ── Jump to a specific message from a full-text search result ───
  async function jumpToMessage(chatId, mId) {
    setSelected(chatId);
    setMessages([]);
    setHasMoreOlder(false);
    isNearBottomRef.current = false;  // we're jumping to a specific spot, not the bottom
    justSwitchedRef.current = true;
    prevMsgCountRef.current = 0;
    setNewMsgCount(0);
    setMobileView('chat');
    setLabelPickerOpen(false);
    setConvSearch('');
    setMsgSearchResults([]);
    await loadMessages(chatId, false, mId);
    loadContact(chatId);
    setHighlightedMsgId(mId);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = messagesContainerRef.current?.querySelector(`[data-msg-id="${mId}"]`);
        if (el) el.scrollIntoView({ block: 'center' });
      }, 60);
    });
    setTimeout(() => setHighlightedMsgId(null), 2200);
  }

  // ── Contact Profile Sidebar data ─────────────────────────────
  async function loadContact(chatId) {
    setContactLoading(true);
    setEventsLoading(true);
    try {
      const res = await getInboxContact(bId, chatId);
      setContact(res.data?.contact ?? null);
      setContactFields(res.data?.fields ?? {});
    } catch { setContact(null); setContactFields({}); }
    setContactLoading(false);

    try {
      const res = await getInboxContactHistory(bId, chatId);
      setContactEvents(res.data?.events ?? []);
    } catch { setContactEvents([]); }
    setEventsLoading(false);
  }

  async function handleSaveContactField(key, value) {
    if (!selected) return;
    try {
      await inboxSetContactField(bId, selected, key, value);
      setContactFields(f => ({ ...f, [key]: value }));
    } catch { toast.error('Failed to save'); }
  }

  function closeChat() {
    setSelected(null);
    setMessages([]);
    setMobileView('list');
    setNewMsgCount(0);
  }

  // ── Escape closes the open chat (or modal, or clears a draft) ─
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (agentModal || cannedModal || labelModal) { setAgentModal(null); setCannedModal(null); setLabelModal(null); return; }
      if (labelPickerOpen) { setLabelPickerOpen(false); return; }
      if (cannedOpen) { setCannedOpen(false); return; }
      if (replyText.trim()) { setReplyText(''); return; }
      if (selected) closeChat();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [agentModal, cannedModal, labelModal, labelPickerOpen, cannedOpen, replyText, selected]);

  // ── Reply ───────────────────────────────────────────────────
  async function handleReply(e) {
    e.preventDefault();
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      if (replyMode === 'note') {
        await inboxAddNote(bId, selected, selectedConv?.cAgentId ?? null, replyText.trim());
      } else {
        await inboxReply(bId, selected, selectedConv?.cAgentId ?? null, replyText.trim(), replyingTo?.waId ?? null);
      }
      setReplyText('');
      setReplyingTo(null);
      isNearBottomRef.current = true; // always show your own reply/note
      await pollNewMessages(selected);
      await loadConversations(true);
    } catch { toast.error(replyMode === 'note' ? 'Failed to add note' : 'Failed to send reply'); }
    setSending(false);
  }

  async function handleReact(msg, emoji) {
    if (!msg.mWaMessageId || !selected) {
      toast.error("Can't react — this message has no WhatsApp ID on record");
      return;
    }
    try {
      await inboxReact(bId, selected, msg.mWaMessageId, emoji);
      isNearBottomRef.current = true;
      await pollNewMessages(selected);
    } catch { toast.error('Failed to send reaction'); }
  }

  // ── Toggle a label on/off the currently selected conversation ───
  // Optimistically updates local state so the chip appears/disappears
  // instantly, then reconciles with the server response.
  async function toggleConversationLabel(lId) {
    if (!selected || !selectedConv) return;
    const current = parseLabelIds(selectedConv.cLabels);
    const next = current.includes(lId) ? current.filter(id => id !== lId) : [...current, lId];
    setConversations(prev => prev.map(c =>
      c.cChatId === selected ? { ...c, cLabels: next.join(',') } : c
    ));
    try {
      await inboxSetLabels(bId, selected, next);
    } catch {
      toast.error('Failed to update labels');
      loadConversations(true); // revert to server truth on failure
    }
  }

  // ── Assign ──────────────────────────────────────────────────
  async function doAssign(agentId) {
    try {
      await inboxAssign(bId, selected, agentId);
      toast.success('Assigned');
      await loadConversations(true);
    } catch { toast.error('Failed to assign'); }
  }
  function handleAssign(agentId) {
    const agent = agents.find(a => String(a.aId) === String(agentId));
    const isReassign = !!selectedConv?.cAgentId;
    setConfirm({
      title: isReassign ? 'Reassign this conversation?' : 'Assign this conversation?',
      message: isReassign
        ? `This conversation is currently assigned to someone else. Reassign it to ${agent?.aName ?? 'this agent'}?`
        : `Assign this conversation to ${agent?.aName ?? 'this agent'}? They'll be notified via WhatsApp and can reply from their agent console.`,
      confirmLabel: isReassign ? 'Reassign' : 'Assign',
      onConfirm: () => { doAssign(agentId); setConfirm(null); },
    });
  }

  // ── Unassign ────────────────────────────────────────────────
  async function doUnassign() {
    try {
      await inboxUnassign(bId, selected);
      toast.success('Agent removed');
      await loadConversations(true);
    } catch { toast.error('Failed to remove agent'); }
  }
  function handleUnassign() {
    setConfirm({
      title: 'Remove assigned agent?',
      message: 'This conversation will go back to the waiting queue for another agent to pick up.',
      confirmLabel: 'Remove agent',
      danger: true,
      onConfirm: () => { doUnassign(); setConfirm(null); },
    });
  }

  // ── Handback to bot ─────────────────────────────────────────
  async function doHandback() {
    try {
      await inboxHandback(bId, selected);
      toast.success('Handed back to bot');
      await loadConversations(true);
      await loadMessages(selected, true);
    } catch { toast.error('Failed'); }
  }
  function handleHandback() {
    setConfirm({
      title: 'Hand back to bot?',
      message: 'The customer will be returned to the automated bot flow, and this conversation will no longer be assigned to an agent.',
      confirmLabel: 'Hand to Bot',
      onConfirm: () => { doHandback(); setConfirm(null); },
    });
  }

  // ── Resolve ─────────────────────────────────────────────────
  async function doResolve() {
    try {
      await inboxResolve(bId, selected);
      toast.success('Resolved');
      closeChat();
      await loadConversations();
    } catch { toast.error('Failed'); }
  }
  function handleResolve() {
    setConfirm({
      title: 'Mark as resolved?',
      message: 'This conversation will be closed and moved out of your open queue.',
      confirmLabel: 'Resolve',
      onConfirm: () => { doResolve(); setConfirm(null); },
    });
  }

  // ── Clear messages (your CmdKit inbox only — never touches WhatsApp) ──
  async function doClearMessages() {
    try {
      await inboxClearMessages(bId, selected);
      toast.success('Chat cleared on your end');
      setMessages([]);
      setHasMoreOlder(false);
      await loadConversations(true);
    } catch { toast.error('Failed to clear messages'); }
  }
  function handleClearMessages() {
    setConfirm({
      title: 'Clear this chat?',
      message: "This only clears the message history here on CmdKit — it never touches WhatsApp itself. " +
        "The customer keeps their own copy of every message on their phone, and they won't see or feel " +
        "anything happen. New messages will keep coming in normally right after this.",
      confirmLabel: 'Clear chat',
      danger: true,
      onConfirm: () => { doClearMessages(); setConfirm(null); },
    });
  }

  // ── Canned response "/" autocomplete ─────────────────────────
  const cannedQuery = matchCannedTrigger(replyText);
  const cannedMatches = cannedQuery !== null ? filterCannedResponses(cannedResponses, cannedQuery) : [];

  function handleReplyTextChange(val) {
    setReplyText(val);
    const q = matchCannedTrigger(val);
    if (replyMode === 'reply' && q !== null && cannedResponses.length > 0) {
      setCannedOpen(true);
      setCannedActiveIndex(0);
    } else {
      setCannedOpen(false);
    }
  }

  function pickCanned(c) {
    setReplyText(c.crBody);
    setCannedOpen(false);
  }

  function handleReplyKeyDown(e) {
    if (cannedOpen && cannedMatches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCannedActiveIndex(i => (i + 1) % cannedMatches.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setCannedActiveIndex(i => (i - 1 + cannedMatches.length) % cannedMatches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickCanned(cannedMatches[cannedActiveIndex]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setCannedOpen(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleReply(e);
    }
  }


  // ── UI-only state & helpers (layout, menus, composer) ───────
  const [moreOpen, setMoreOpen]   = useState(false);   // ⋯ menu (desktop dropdown / mobile sheet)
  const [vv, setVv]               = useState(null);    // mobile visual viewport (keeps composer above the keyboard)
  const textareaRef               = useRef(null);
  const showMobileChat    = isMobile && !!selected && mobileView === 'chat';
  const showMobileContact = isMobile && !!selected && mobileView === 'contact';

  // ── Phone back button (Android back / iOS swipe) ───────────
  // Opening a chat (and then the contact page) on a phone pushes a history entry,
  // so the device's Back closes them one at a time — like WhatsApp — instead of
  // leaving the inbox. The on-screen back arrow goes through history too, so the
  // two never get out of step.
  const histDepthRef = useRef(0);        // entries we've pushed
  const histSkipRef  = useRef(0);        // pops we caused ourselves
  const closeChatRef = useRef(null);
  closeChatRef.current = () => { setSelected(null); setMessages([]); setMobileView('list'); setNewMsgCount(0); };
  const wantDepth = !isMobile ? 0 : showMobileContact ? 2 : showMobileChat ? 1 : 0;
  useEffect(() => {
    while (histDepthRef.current < wantDepth) {
      histDepthRef.current += 1;
      window.history.pushState({ ...(window.history.state || {}), ibx: histDepthRef.current }, '');
    }
    if (histDepthRef.current > wantDepth) {      // closed some other way (Resolve, Escape…) → drop our entries
      const n = histDepthRef.current - wantDepth;
      histDepthRef.current = wantDepth;
      histSkipRef.current += n;
      window.history.go(-n);
    }
  }, [wantDepth]);
  useEffect(() => {
    function onPop() {
      if (histSkipRef.current > 0) { histSkipRef.current -= 1; return; }
      if (histDepthRef.current <= 0) return;
      histDepthRef.current -= 1;
      if (histDepthRef.current >= 1) setMobileView('chat');
      else closeChatRef.current();
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const mobileBack = () => { if (histDepthRef.current > 0) window.history.back(); else closeChatRef.current(); };

  // Auto-grow the reply box up to ~5 lines
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [replyText, selected, replyMode]);

  // On phones the open chat is a full-screen layer. Track the *visual*
  // viewport so the composer stays pinned right above the on-screen
  // keyboard on iOS/Android instead of being covered by it.
  useEffect(() => {
    const v = window.visualViewport;
    if (!(showMobileChat || showMobileContact) || !v) { setVv(null); return undefined; }
    const update = () => setVv({ h: v.height, top: v.offsetTop });
    update();
    v.addEventListener('resize', update);
    v.addEventListener('scroll', update);
    return () => { v.removeEventListener('resize', update); v.removeEventListener('scroll', update); };
  }, [showMobileChat, showMobileContact]);

  // Lock the page behind the full-screen chat on mobile
  useEffect(() => {
    if (!(showMobileChat || showMobileContact)) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showMobileChat, showMobileContact]);

  // Escape closes the ⋯ menu first
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = e => { if (e.key === 'Escape') { e.stopImmediatePropagation(); setMoreOpen(false); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [moreOpen]);

  useEffect(() => { setMoreOpen(false); }, [selected]);

  const fullscreenStyle = vv ? { height: vv.h, top: vv.top } : undefined;
  const selectedName    = selectedConv?.cName || selected || '';
  const selectedLabels  = selectedConv ? labelsForConversation(selectedConv, labelsById) : [];
  const assignedAgent   = agents.find(a => String(a.aId) === String(selectedConv?.cAgentId));

  function handleComposerKeyDown(e) {
    // On phones Enter inserts a new line (like WhatsApp); send with the button.
    if (isMobile && e.key === 'Enter' && !(cannedOpen && cannedMatches.length > 0)) return;
    handleReplyKeyDown(e);
  }

  const SIDE_TABS = [
    { key: 'conversations', label: 'Chats',   icon: 'fa-comments',     badge: waitingCount, onClick: () => setActiveTab('conversations') },
    { key: 'agents',        label: 'Agents',  icon: 'fa-user-group',   onClick: () => { setActiveTab('agents'); loadAgents(); } },
    { key: 'canned',        label: 'Replies', icon: 'fa-bolt',         onClick: () => { setActiveTab('canned'); loadCannedResponses(); } },
    { key: 'labels',        label: 'Labels',  icon: 'fa-tag',          onClick: () => { setActiveTab('labels'); loadLabels(); } },
    { key: 'reports',       label: 'Reports', icon: 'fa-chart-simple', onClick: () => { setActiveTab('reports'); loadReports(); } },
  ];

  const MODE_FILTERS = [
    { key: '',        label: 'All' },
    { key: 'waiting', label: 'Waiting' },
    { key: 'agent',   label: 'With agent' },
    { key: 'bot',     label: 'Bot' },
  ];

  // ── Sidebar (conversation list + management panels) ─────────
  const sidebar = (
    <aside className="inbox-sidebar ibx-side">
      <div className="ibx-side-head">
        <div className="ibx-side-title">
          <h2>Inbox</h2>
          <span>
            {conversations.length} open
            {waitingCount > 0 && <> · <b className="ibx-waiting-txt">{waitingCount} waiting</b></>}
          </span>
        </div>
        <button
          type="button"
          className={`ibx-icon-btn ${loading ? 'is-spinning' : ''}`}
          onClick={() => loadConversations()}
          title="Refresh"
          aria-label="Refresh"
        >
          <i className="fa-solid fa-rotate-right" />
        </button>
      </div>

      <nav className="ibx-tabs" aria-label="Inbox sections">
        {SIDE_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            className={`ibx-tab ${activeTab === t.key ? 'is-active' : ''}`}
            onClick={t.onClick}
          >
            <i className={`fa-solid ${t.icon}`} />
            <span>{t.label}</span>
            {!!t.badge && <em className="ibx-tab-badge">{t.badge}</em>}
          </button>
        ))}
      </nav>

      {activeTab === 'conversations' && (
        <>
          <div className="ibx-search">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              placeholder="Search name, phone or message"
              value={convSearch}
              onChange={e => setConvSearch(e.target.value)}
            />
            {convSearch && (
              <button type="button" className="ibx-search-clear" onClick={() => setConvSearch('')} aria-label="Clear search">
                <i className="fa-solid fa-xmark" />
              </button>
            )}
          </div>

          {/* Server-side full-text search across message content */}
          {convSearch.trim().length >= 2 && (
            <div className="ibx-msg-results">
              <div className="ibx-section-label">
                Messages {msgSearchLoading && <span className="ibx-dot-pulse" />}
              </div>
              {!msgSearchLoading && msgSearchResults.length === 0 ? (
                <div className="ibx-msg-results-empty">No messages contain “{convSearch.trim()}”</div>
              ) : (
                <ul>
                  {msgSearchResults.map(r => r.matches.map(m => (
                    <li key={m.mId}>
                      <button type="button" onClick={() => jumpToMessage(r.chatId, m.mId)}>
                        <b>
                          {r.cName || r.chatId}
                          {r.matchCount > 1 && <span> · {r.matchCount} matches</span>}
                        </b>
                        <span>{m.mText}</span>
                      </button>
                    </li>
                  )))}
                </ul>
              )}
            </div>
          )}

          <div className="ibx-filters">
            {MODE_FILTERS.map(f => (
              <button
                key={f.key || 'all'}
                type="button"
                className={`ibx-chip ${filterMode === f.key ? 'is-active' : ''}`}
                onClick={() => setFilterMode(f.key)}
              >
                {f.label}
              </button>
            ))}
            {labels.length > 0 && <span className="ibx-filters-sep" />}
            {labels.map(l => {
              const active = labelFilter === l.lId;
              return (
                <button
                  key={l.lId}
                  type="button"
                  className={`ibx-chip ibx-chip-label ${active ? 'is-active' : ''}`}
                  style={active ? { background: l.lColor, borderColor: l.lColor, color: '#fff' } : undefined}
                  onClick={() => setLabelFilter(f => f === l.lId ? 0 : l.lId)}
                >
                  <span className="ibx-chip-dot" style={{ background: active ? '#fff' : l.lColor }} />
                  {l.lName}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="ibx-skeleton-list">
              {[0, 1, 2, 3, 4, 5].map(i => (
                <div key={i} className="ibx-skeleton-row"><span /><div><i /><i /></div></div>
              ))}
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="ibx-empty-list">
              <span className="ibx-empty-ic"><i className="fa-regular fa-comments" /></span>
              <b>{convSearch.trim() ? 'No matching chats' : 'No open conversations'}</b>
              <span>{convSearch.trim() ? 'Try a different name or number.' : 'New chats from your bot will show up here.'}</span>
            </div>
          ) : (
            <>
              <ul className="inbox-conv-list">
                {filteredConversations.map(conv => {
                  const convLabels = labelsForConversation(conv, labelsById);
                  const name = conv.cName || conv.cChatId;
                  return (
                    <li
                      key={conv.cChatId}
                      className={`inbox-conv-item ${selected === conv.cChatId ? 'active' : ''} ${conv.cMode === 'waiting' ? 'is-waiting' : ''}`}
                      onClick={() => selectConversation(conv.cChatId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter') selectConversation(conv.cChatId); }}
                    >
                      <div className="conv-avatar" style={{ background: avatarColor(name) }}>
                        {name[0]?.toUpperCase()}
                      </div>
                      <div className="conv-meta">
                        <div className="conv-top">
                          <span className="conv-name">
                            {String(conv.cChatId).startsWith('web:') && <i className="fa-solid fa-globe" title="Website chat" aria-label="Website chat" style={{ marginRight: 6, fontSize: '.85em', opacity: 0.7 }} />}
                            {String(conv.cChatId).startsWith('web:') && name === conv.cChatId ? 'Website visitor' : name}
                          </span>
                          <span className="conv-time">{timeAgo(conv.cLastMsgAt)}</span>
                        </div>
                        <div className="conv-bottom">
                          <span className="conv-preview">{conv.cLastMsg || 'No messages yet'}</span>
                          {modeBadge(conv.cMode)}
                        </div>
                        {convLabels.length > 0 && (
                          <div className="conv-labels">
                            {convLabels.map(l => <LabelChip key={l.lId} label={l} small />)}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
                {hasMoreConvs && !convSearch.trim() && (
                  <li className="ibx-load-more-wrap">
                    <button
                      type="button"
                      className="inbox-load-more-convs-btn"
                      onClick={loadMoreConversations}
                      disabled={loadingMoreConvs}
                    >
                      {loadingMoreConvs ? 'Loading…' : 'Load more conversations'}
                    </button>
                  </li>
                )}
              </ul>
            </>
          )}
        </>
      )}

      {activeTab === 'agents' && (
        <AgentsPanel bId={bId} agents={agents} onReload={loadAgents} agentModal={agentModal} setAgentModal={setAgentModal} />
      )}
      {activeTab === 'canned' && (
        <CannedResponsesPanel bId={bId} cannedResponses={cannedResponses} onReload={loadCannedResponses} cannedModal={cannedModal} setCannedModal={setCannedModal} />
      )}
      {activeTab === 'labels' && (
        <LabelsPanel bId={bId} labels={labels} onReload={loadLabels} labelModal={labelModal} setLabelModal={setLabelModal} />
      )}
      {activeTab === 'reports' && (
        <ReportsPanel data={reportsData} loading={reportsLoading} range={reportsRange} onRangeChange={r => { setReportsRange(r); loadReports(r); }} />
      )}
    </aside>
  );

  // ── ⋯ menu contents ──────────────────────────────────────────
  const closeMore = () => setMoreOpen(false);
  const moreMenu = moreOpen && selected && (
    isMobile ? (
      <div className="ibx-sheet-backdrop" onClick={closeMore}>
        <div className="ibx-sheet" onClick={e => e.stopPropagation()} role="dialog" aria-label="Conversation actions">
          <span className="ibx-sheet-grip" />
          <div className="ibx-sheet-head">
            <div className="conv-avatar" style={{ background: avatarColor(selectedName) }}>{selectedName[0]?.toUpperCase()}</div>
            <div>
              <b>{selectedName}</b>
              <span>{selected}</span>
            </div>
          </div>

          <button type="button" className="ibx-sheet-item" onClick={() => { closeMore(); setMobileView('contact'); }}>
            <i className="fa-regular fa-id-card" /> Contact details
          </button>

          {labels.length > 0 && (
            <div className="ibx-sheet-group">
              <div className="ibx-section-label">Labels</div>
              <div className="ibx-sheet-labels">
                {labels.map(l => {
                  const on = parseLabelIds(selectedConv?.cLabels).includes(l.lId);
                  return (
                    <button
                      key={l.lId}
                      type="button"
                      className={`ibx-chip ibx-chip-label ${on ? 'is-active' : ''}`}
                      style={on ? { background: l.lColor, borderColor: l.lColor, color: '#fff' } : undefined}
                      onClick={() => toggleConversationLabel(l.lId)}
                    >
                      <span className="ibx-chip-dot" style={{ background: on ? '#fff' : l.lColor }} />
                      {l.lName}
                      {on && <i className="fa-solid fa-check" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {agents.length > 0 && (
            <div className="ibx-sheet-group">
              <div className="ibx-section-label">Assigned agent</div>
              {agents.map(a => {
                const on = String(a.aId) === String(selectedConv?.cAgentId);
                return (
                  <button
                    key={a.aId}
                    type="button"
                    className={`ibx-sheet-item ${on ? 'is-on' : ''}`}
                    onClick={() => { if (!on) { closeMore(); handleAssign(a.aId); } }}
                  >
                    <span className="ibx-mini-av">{a.aName?.[0]?.toUpperCase()}</span>
                    {a.aName}
                    {on && <i className="fa-solid fa-check ibx-sheet-check" />}
                  </button>
                );
              })}
              {selectedConv?.cAgentId && (
                <button type="button" className="ibx-sheet-item" onClick={() => { closeMore(); handleUnassign(); }}>
                  <i className="fa-solid fa-user-xmark" /> Remove assigned agent
                </button>
              )}
            </div>
          )}

          <div className="ibx-sheet-group">
            <button type="button" className="ibx-sheet-item" onClick={() => { closeMore(); handleHandback(); }}>
              <i className="fa-solid fa-robot" /> Hand back to bot
            </button>
            <button type="button" className="ibx-sheet-item is-danger" onClick={() => { closeMore(); handleClearMessages(); }}>
              <i className="fa-solid fa-broom" /> Clear chat history
            </button>
          </div>

          <button type="button" className="ibx-sheet-cancel" onClick={closeMore}>Cancel</button>
        </div>
      </div>
    ) : (
      <>
        <div className="ibx-menu-catch" onClick={closeMore} />
        <div className="ibx-menu" role="menu">
          <button type="button" className="ibx-menu-item" onClick={() => { closeMore(); setContactOpen(o => !o); }}>
            <i className="fa-regular fa-id-card" /> {contactOpen ? 'Hide contact details' : 'Show contact details'}
          </button>
          <button type="button" className="ibx-menu-item ibx-menu-narrow" onClick={() => { closeMore(); handleHandback(); }}>
            <i className="fa-solid fa-robot" /> Hand back to bot
          </button>
          {agents.length > 0 && (
            <div className="ibx-menu-group ibx-menu-narrow">
              <div className="ibx-section-label">Assign to</div>
              {agents.map(a => {
                const on = String(a.aId) === String(selectedConv?.cAgentId);
                return (
                  <button key={a.aId} type="button" className={`ibx-menu-item ${on ? 'is-on' : ''}`}
                    onClick={() => { closeMore(); if (!on) handleAssign(a.aId); }}>
                    <span className="ibx-mini-av">{a.aName?.[0]?.toUpperCase()}</span> {a.aName}
                    {on && <i className="fa-solid fa-check ibx-sheet-check" />}
                  </button>
                );
              })}
            </div>
          )}
          {selectedConv?.cAgentId && (
            <button type="button" className="ibx-menu-item" onClick={() => { closeMore(); handleUnassign(); }}>
              <i className="fa-solid fa-user-xmark" /> Remove assigned agent
            </button>
          )}
          <span className="ibx-menu-sep" />
          <button type="button" className="ibx-menu-item is-danger" onClick={() => { closeMore(); handleClearMessages(); }}>
            <i className="fa-solid fa-broom" /> Clear chat history
          </button>
        </div>
      </>
    )
  );

  // ── Messages with day separators ────────────────────────────
  const threadItems = [];
  let lastDay = null;
  messages.forEach(m => {
    const d = m.mCreatedAt ? new Date(m.mCreatedAt) : null;
    const key = d && !isNaN(d) ? d.toDateString() : null;
    if (key && key !== lastDay) {
      threadItems.push(<div key={`day-${key}`} className="ibx-day"><span>{dayLabel(d)}</span></div>);
      lastDay = key;
    }
    threadItems.push(
      <MessageBubble
        key={m.mId}
        msg={m}
        bId={bId}
        highlighted={m.mId === highlightedMsgId}
        onReply={() => {
          setReplyMode('reply');
          setReplyingTo({
            waId: m.mWaMessageId,
            text: m.mText || (m.mType ? `[${m.mType}]` : ''),
            senderLabel: m.mDirection === 'out' ? (m.agentName || 'You') : (selectedConv?.cName || 'Customer'),
          });
          textareaRef.current?.focus();
        }}
        onReact={emoji => handleReact(m, emoji)}
      />
    );
  });

  // ── Chat panel ───────────────────────────────────────────────
  const chatPanel = !selected ? (
    <main className="inbox-chat ibx-chat">
      <div className="ibx-placeholder">
        <div className="ibx-placeholder-art" aria-hidden="true">
          <span className="b1" /><span className="b2" /><span className="b3" />
        </div>
        <h3>Pick a conversation</h3>
        <p>Choose a chat from the list to read the thread, reply to the customer or leave a private note for your team.</p>
        {waitingCount > 0 && (
          <button type="button" className="ibx-btn ibx-btn-soft" onClick={() => setFilterMode('waiting')}>
            <i className="fa-solid fa-hourglass-half" /> {waitingCount} waiting for an agent
          </button>
        )}
      </div>
    </main>
  ) : (
    <main
      className={`inbox-chat ibx-chat ${isMobile ? 'is-fullscreen' : ''}`}
      style={isMobile ? fullscreenStyle : undefined}
    >
      <header className="ibx-chat-head">
        <div className="ibx-chat-bar">
          {isMobile && (
            <button type="button" className="ibx-icon-btn ibx-back" onClick={mobileBack} aria-label="Back to conversations">
              <i className="fa-solid fa-arrow-left" />
            </button>
          )}
          <button
            type="button"
            className="ibx-who"
            onClick={() => isMobile ? setMobileView('contact') : setContactOpen(o => !o)}
            title="Contact details"
          >
            <span className="conv-avatar lg" style={{ background: avatarColor(selectedName) }}>
              {selectedName[0]?.toUpperCase()}
            </span>
            <span className="ibx-who-text">
              <b>{selectedName}</b>
              <span>
                {selectedConv && modeBadge(selectedConv.cMode)}
                {assignedAgent
                  ? <em><i className="fa-solid fa-headset" /> {assignedAgent.aName}</em>
                  : <em>{prettyPhone(selected)}</em>}
              </span>
            </span>
          </button>

          <div className="ibx-chat-actions">
            {!isMobile && agents.length > 0 && (
              <label className="ibx-select" title="Assign agent">
                <i className="fa-solid fa-user-plus" />
                <select
                  value={selectedConv?.cAgentId ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '__unassign__') handleUnassign();
                    else if (v) handleAssign(v);
                  }}
                >
                  <option value="">Assign agent</option>
                  {agents.map(a => <option key={a.aId} value={a.aId}>{a.aName}</option>)}
                  {selectedConv?.cAgentId && <option value="__unassign__">Remove assigned agent</option>}
                </select>
              </label>
            )}
            {!isMobile && (
              <button type="button" className="ibx-btn" onClick={handleHandback} title="Hand back to the bot">
                <i className="fa-solid fa-robot" /> <span>Hand to bot</span>
              </button>
            )}
            <button type="button" className="ibx-btn ibx-btn-primary" onClick={handleResolve} title="Mark as resolved">
              <i className="fa-solid fa-check" /> <span className="ibx-hide-xs">Resolve</span>
            </button>
            <div className="ibx-more-wrap">
              <button
                type="button"
                className={`ibx-icon-btn ${moreOpen ? 'is-on' : ''}`}
                onClick={() => setMoreOpen(o => !o)}
                aria-label="More actions"
                aria-expanded={moreOpen}
              >
                <i className="fa-solid fa-ellipsis-vertical" />
              </button>
              {!isMobile && moreMenu}
            </div>
            {!isMobile && (
              <>
                <button
                  type="button"
                  className={`ibx-icon-btn ${contactOpen ? 'is-on' : ''}`}
                  onClick={() => setContactOpen(o => !o)}
                  title="Contact details"
                  aria-label="Toggle contact details"
                >
                  <i className="fa-regular fa-id-card" />
                </button>
                <button type="button" className="ibx-icon-btn" onClick={closeChat} title="Close chat (Esc)" aria-label="Close chat">
                  <i className="fa-solid fa-xmark" />
                </button>
              </>
            )}
          </div>
        </div>

        {(!isMobile || selectedLabels.length > 0) && (
          <div className="ibx-label-row">
            {selectedLabels.map(l => (
              <LabelChip key={l.lId} label={l} onRemove={() => toggleConversationLabel(l.lId)} />
            ))}
            {!isMobile && (
              <div className="ibx-label-add-wrap">
                <button type="button" className="ibx-label-add" onClick={() => setLabelPickerOpen(o => !o)}>
                  <i className="fa-solid fa-plus" /> Label
                </button>
                {labelPickerOpen && (
                  <LabelPickerPopover
                    labels={labels}
                    selectedIds={parseLabelIds(selectedConv?.cLabels)}
                    onToggle={toggleConversationLabel}
                    onClose={() => setLabelPickerOpen(false)}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </header>

      <div className="ibx-thread">
        <div
          className="inbox-messages"
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
        >
          {loadingOlder && <div className="inbox-loading-older">Loading earlier messages…</div>}
          {!msgLoading && !loadingOlder && hasMoreOlder && messages.length > 0 && (
            <button type="button" className="inbox-load-older-btn" onClick={() => loadOlderMessages(selected)}>
              Load earlier messages
            </button>
          )}
          {msgLoading ? (
            <div className="ibx-thread-loading">
              <span className="ibx-sk-bubble in" /><span className="ibx-sk-bubble out" />
              <span className="ibx-sk-bubble in short" /><span className="ibx-sk-bubble out short" />
            </div>
          ) : messages.length === 0 ? (
            <div className="ibx-thread-empty">
              <i className="fa-regular fa-comment-dots" />
              <span>No messages here yet</span>
            </div>
          ) : threadItems}
          <div ref={messagesEndRef} />
        </div>

        {newMsgCount > 0 && (
          <button type="button" className="inbox-jump-btn" onClick={jumpToBottom}>
            <i className="fa-solid fa-arrow-down" /> {newMsgCount} new message{newMsgCount > 1 ? 's' : ''}
          </button>
        )}
      </div>

      <div className={`ibx-composer ${replyMode === 'note' ? 'is-note' : ''}`}>
        {replyingTo && replyMode === 'reply' && (
          <div className="inbox-reply-preview">
            <div className="inbox-reply-preview-bar" />
            <div className="inbox-reply-preview-body">
              <div className="inbox-reply-preview-label">Replying to {replyingTo.senderLabel}</div>
              <div className="inbox-reply-preview-text">{replyingTo.text}</div>
            </div>
            <button type="button" className="inbox-reply-preview-close" onClick={() => setReplyingTo(null)} aria-label="Cancel reply">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        )}

        <div className="ibx-mode">
          <button
            type="button"
            className={`ibx-mode-btn ${replyMode === 'reply' ? 'is-active' : ''}`}
            onClick={() => setReplyMode('reply')}
          >
            <i className="fa-solid fa-reply" /> Reply
          </button>
          <button
            type="button"
            className={`ibx-mode-btn is-note-btn ${replyMode === 'note' ? 'is-active' : ''}`}
            onClick={() => { setReplyMode('note'); setReplyingTo(null); setCannedOpen(false); }}
          >
            <i className="fa-solid fa-lock" /> Private note
          </button>
          {replyMode === 'reply' && cannedResponses.length > 0 && !isMobile && (
            <span className="ibx-mode-hint">Type <kbd>/</kbd> for saved replies</span>
          )}
        </div>

        <form className="ibx-compose-row" onSubmit={handleReply}>
          {cannedOpen && replyMode === 'reply' && (
            <CannedResponseDropdown
              items={cannedMatches}
              activeIndex={cannedActiveIndex}
              onPick={pickCanned}
              onHoverIndex={setCannedActiveIndex}
            />
          )}
          <textarea
            ref={textareaRef}
            className="ibx-input"
            placeholder={replyMode === 'note' ? 'Write a note only your team can see…' : 'Type a message'}
            value={replyText}
            rows={1}
            onChange={e => handleReplyTextChange(e.target.value)}
            onKeyDown={handleComposerKeyDown}
          />
          <button
            type="submit"
            className="ibx-send"
            aria-label={replyMode === 'note' ? 'Add note' : 'Send message'}
            title={replyMode === 'note' ? 'Add note' : 'Send (Enter)'}
            disabled={sending || !replyText.trim()}
          >
            {sending
              ? <span className="ibx-spinner" />
              : <i className={`fa-solid ${replyMode === 'note' ? 'fa-note-sticky' : 'fa-paper-plane'}`} />}
          </button>
        </form>
      </div>

      {isMobile && moreMenu}
    </main>
  );

  const contactPanel = selected && (
    <ContactSidebar
      loading={contactLoading}
      contact={contact}
      fields={contactFields}
      events={contactEvents}
      eventsLoading={eventsLoading}
      chatId={selected}
      convName={selectedConv?.cName}
      convLabels={selectedConv?.cLabels}
      allLabels={labels}
      onToggleLabel={toggleConversationLabel}
      onSaveField={handleSaveContactField}
      onClose={isMobile ? mobileBack : () => setContactOpen(false)}
      fullWidth={isMobile}
    />
  );

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className={`inbox-wrap ibx ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
      {isMobile ? (
        <>
          {sidebar}
          {/* The open chat is a full-screen layer. It's rendered at the end of <body> so no
              parent stacking context (the inbox page shell, the app's sticky top bar) can sit
              on top of its header and hide the back button. */}
          {(showMobileChat || showMobileContact || confirm) && createPortal(
            <div className="inbox-wrap ibx is-mobile ibx-portal">
              {showMobileChat && chatPanel}
              {showMobileContact && (
                <div className="ibx-fullscreen ibx-contact-screen" style={fullscreenStyle}>{contactPanel}</div>
              )}
              {confirm && (
                <ConfirmModal
                  title={confirm.title}
                  message={confirm.message}
                  confirmLabel={confirm.confirmLabel}
                  danger={confirm.danger}
                  onConfirm={confirm.onConfirm}
                  onCancel={() => setConfirm(null)}
                />
              )}
            </div>,
            document.body,
          )}
        </>
      ) : (
        <>
          {sidebar}
          {chatPanel}
          {selected && contactOpen && <div className="ibx-contact-col">{contactPanel}</div>}
        </>
      )}

      {!isMobile && confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function dayLabel(d) {
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

// ── Message bubble ────────────────────────────────────────────
function MessageBubble({ msg, bId, onReply, onReact, highlighted }) {
  const isOut   = msg.mDirection === 'out';
  const isAgent = msg.mSenderType === 'agent';
  const isNote  = msg.mSenderType === 'note';

  const REACT_EMOJIS = ['👍','❤️','😂','😮','😢','🙏','🔥','🎉','👏','😍','🤔','😅','😎','💯','✅','❌','😡','🥳','👀','🙌'];

  // ctxMenu: { mode: 'menu' | 'emoji', x, y } — viewport coordinates (position:fixed)
  const [ctxMenu, setCtxMenu] = useState(null);
  const [swipeX, setSwipeX] = useState(0);
  const touch = useRef({ startX: 0, startY: 0, swiping: false, longPressTimer: null });

  const canAct = !!msg.mWaMessageId && (onReply || onReact);

  function openMenuAt(x, y, mode = 'menu') {
    const clampedX = Math.min(Math.max(x, 8), window.innerWidth - 220);
    const clampedY = Math.min(Math.max(y, 8), window.innerHeight - 220);
    setCtxMenu({ mode, x: Math.max(clampedX, 8), y: Math.max(clampedY, 8) });
  }

  function handleContextMenu(e) {
    if (!canAct) return;
    e.preventDefault();
    openMenuAt(e.clientX, e.clientY);
  }

  function handleTouchStart(e) {
    if (!canAct) return;
    const t = e.touches[0];
    touch.current.startX = t.clientX;
    touch.current.startY = t.clientY;
    touch.current.swiping = false;
    touch.current.longPressTimer = onReact ? setTimeout(() => {
      if (touch.current.swiping) return;
      if (navigator.vibrate) navigator.vibrate(12);
      openMenuAt(t.clientX, t.clientY, 'emoji');
    }, 450) : null;
  }

  function handleTouchMove(e) {
    if (!canAct) return;
    const t = e.touches[0];
    const dx = t.clientX - touch.current.startX;
    const dy = t.clientY - touch.current.startY;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      if (touch.current.longPressTimer) { clearTimeout(touch.current.longPressTimer); touch.current.longPressTimer = null; }
    }
    if (dx > 8 && Math.abs(dx) > Math.abs(dy)) {
      touch.current.swiping = true;
      setSwipeX(Math.min(dx, 70));
    }
  }

  function handleTouchEnd() {
    if (touch.current.longPressTimer) { clearTimeout(touch.current.longPressTimer); touch.current.longPressTimer = null; }
    if (touch.current.swiping && swipeX > 50 && onReply) onReply();
    touch.current.swiping = false;
    setSwipeX(0);
  }

  useEffect(() => {
    if (!ctxMenu) return undefined;
    function close() { setCtxMenu(null); }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    document.addEventListener('scroll', close, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('scroll', close, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);

  const [imgSrc, setImgSrc]     = useState(msg.mMediaUrl || null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (msg.mMediaUrl) { setImgSrc(msg.mMediaUrl); return; }
    const mediaTypes = ['image','video','audio','document','sticker'];
    if (!msg.mMediaId || !mediaTypes.includes(msg.mType) || fetching) return;
    setFetching(true);
    inboxFetchMedia(bId, msg.mChatId, msg.mId, msg.mMediaId)
      .then(r => { if (r.data?.url) setImgSrc(r.data.url); })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [msg.mId, msg.mMediaUrl, msg.mMediaId, msg.mType, bId]); // eslint-disable-line react-hooks/exhaustive-deps

  const time = msg.mCreatedAt
    ? new Date(msg.mCreatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div
      className={`msg-bubble-wrap ${isOut ? 'out' : 'in'} ${isNote ? 'is-note' : ''}`}
      data-msg-id={msg.mId}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {swipeX > 0 && (
        <div className="msg-swipe-reply-icon" style={{ opacity: Math.min(swipeX / 50, 1) }}>
          <i className="fa-solid fa-reply" />
        </div>
      )}

      <div
        className={`msg-bubble ${isAgent ? 'agent-bubble' : ''} ${isNote ? 'note-bubble' : ''} ${highlighted ? 'is-highlighted' : ''}`}
        style={swipeX ? { transform: `translateX(${swipeX}px)` } : undefined}
      >
        {isNote && (
          <div className="msg-note-tag">
            <i className="fa-solid fa-lock" /> Private note{msg.agentName ? ` · ${msg.agentName}` : ''}
          </div>
        )}
        {isAgent && !isNote && (
          <div className="msg-agent-label"><i className="fa-solid fa-headset" /> {msg.agentName || 'Agent'}</div>
        )}
        {isOut && !isAgent && !isNote && (
          <div className="msg-agent-label msg-bot-label"><i className="fa-solid fa-robot" /> Bot</div>
        )}

        {msg.mReplyToWaId && msg.replyPreviewText && (
          <div className="msg-quote-preview">
            <div className="msg-quote-bar" />
            <div className="msg-quote-text">{msg.replyPreviewText}</div>
          </div>
        )}

        {msg.mType === 'image' && (
          imgSrc
            ? <img src={imgSrc} alt="" className="msg-media-img" onClick={() => window.open(imgSrc)} />
            : <div className="msg-media-loading"><i className="fa-regular fa-image" /> Loading image…</div>
        )}
        {msg.mType === 'video' && imgSrc && <video src={imgSrc} controls className="msg-media-video" />}
        {msg.mType === 'audio' && imgSrc && <audio src={imgSrc} controls className="msg-media-audio" />}
        {msg.mType === 'document' && imgSrc && (
          <a href={imgSrc} target="_blank" rel="noreferrer" className="msg-doc-link">
            <span className="msg-doc-ic"><i className="fa-regular fa-file-lines" /></span>
            <span>{msg.mMediaName || 'Document'}</span>
            <i className="fa-solid fa-download" />
          </a>
        )}
        {msg.mType === 'location' && msg.mLat && (
          <a href={`https://maps.google.com/?q=${msg.mLat},${msg.mLng}`} target="_blank" rel="noreferrer" className="msg-location-link">
            <i className="fa-solid fa-location-dot" /> View location
          </a>
        )}
        {msg.mType === 'sticker' && imgSrc && <img src={imgSrc} alt="" className="msg-sticker" />}

        {msg.mText && <p className="msg-text">{msg.mText}</p>}

        <span className="msg-time">{time}</span>
      </div>

      {ctxMenu && (
        <div
          className="msg-ctx-menu"
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
        >
          {ctxMenu.mode === 'menu' && (
            <>
              {onReply && (
                <button type="button" className="msg-ctx-item" onClick={() => { onReply(); setCtxMenu(null); }}>
                  <i className="fa-solid fa-reply" /> Reply
                </button>
              )}
              {onReact && (
                <button type="button" className="msg-ctx-item" onClick={() => setCtxMenu(m => ({ ...m, mode: 'emoji' }))}>
                  <i className="fa-regular fa-face-smile" /> React
                </button>
              )}
            </>
          )}
          {ctxMenu.mode === 'emoji' && onReact && (
            <div className="msg-ctx-emoji-grid">
              {REACT_EMOJIS.map(e => (
                <button key={e} type="button" className="msg-react-emoji" onClick={() => { onReact(e); setCtxMenu(null); }}>{e}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Agents management panel ───────────────────────────────────
function AgentsPanel({ bId, agents, onReload, agentModal, setAgentModal }) {
  const [form, setForm]         = useState({ name: '', email: '', password: '' });
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | agent obj

  async function handleSave() {
    if (!form.name || !form.email || (!agentModal?.aId && !form.password)) {
      toast.error('Name, email and password required'); return;
    }
    setSaving(true);
    try {
      if (agentModal?.aId) {
        await inboxUpdateAgent(bId, { agentId: agentModal.aId, ...form });
        toast.success('Agent updated');
      } else {
        await inboxCreateAgent(bId, form);
        toast.success('Agent created');
      }
      setAgentModal(null);
      setForm({ name: '', email: '', password: '' });
      onReload();
    } catch { toast.error('Failed to save agent'); }
    setSaving(false);
  }

  async function doDelete(agent) {
    try {
      await inboxDeleteAgent(bId, agent.aId);
      toast.success('Agent deleted');
      onReload();
    } catch { toast.error('Failed to delete'); }
  }

  async function handleCopyInviteLink() {
    const link = `${window.location.origin}/agent?bId=${encodeURIComponent(bId)}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied — send it with the agent\'s email and password');
    } catch {
      window.prompt('Copy this invite link:', link);
    }
  }

  return (
    <div className="agents-panel">
      <div className="agents-panel-header">
        <span className="agents-panel-header-title">Agents ({agents.length})</span>
        <div className="agents-panel-header-actions">
          <button className="btn btn-sm btn-outline" onClick={handleCopyInviteLink}>
            <i className="fas fa-link" /> Invite link
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => { setForm({ name: '', email: '', password: '' }); setAgentModal('create'); }}
          >
            <i className="fas fa-plus" /> Add Agent
          </button>
        </div>
      </div>

      <div className="ibx-panel-hint">
        Send each agent the invite link above along with the email and
        password you set for them — it pre-fills the Bot ID so they only
        need to type their email and password.
      </div>

      {agents.length === 0 ? (
        <div className="inbox-empty">No agents yet. Add one above.</div>
      ) : (
        <ul className="agent-list">
          {agents.map(a => (
            <li key={a.aId} className="agent-item">
              <div className="agent-avatar" style={{ background: avatarColor(a.aName) }}>{a.aName[0].toUpperCase()}</div>
              <div className="agent-meta">
                <div className="agent-name">{a.aName}</div>
                <div className="agent-email">{a.aEmail}</div>
              </div>
              <div className="agent-actions">
                <button className="icon-btn" onClick={() => {
                  setForm({ name: a.aName, email: a.aEmail, password: '' });
                  setAgentModal(a);
                }} aria-label="Edit"><i className="fa-solid fa-pen" /></button>
                <button className="icon-btn danger" onClick={() => setConfirmDelete(a)} aria-label="Delete"><i className="fa-regular fa-trash-can" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Agent modal */}
      {agentModal && (
        <div className="modal-overlay" onClick={() => setAgentModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{agentModal?.aId ? 'Edit Agent' : 'Add Agent'}</h3>
              <button className="icon-btn" onClick={() => setAgentModal(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <label className="field-label">Name</label>
              <input
                className="field-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Agent name"
              />
              <label className="field-label">Email</label>
              <input
                className="field-input"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="agent@example.com"
              />
              <label className="field-label">
                Password {agentModal?.aId && <span className="text-muted">(leave blank to keep)</span>}
              </label>
              <input
                className="field-input"
                type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="••••••••"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAgentModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Agent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete agent?"
          message={`"${confirmDelete.aName}" will lose access immediately and any conversations assigned to them will need to be reassigned.`}
          confirmLabel="Delete agent"
          danger
          onConfirm={() => { doDelete(confirmDelete); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ── Canned responses management panel ──────────────────────────
function CannedResponsesPanel({ bId, cannedResponses, onReload, cannedModal, setCannedModal }) {
  const [form, setForm]         = useState({ shortcut: '', body: '' });
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | canned obj

  async function handleSave() {
    const shortcut = form.shortcut.trim().replace(/^\/+/, '');
    const body     = form.body.trim();
    if (!shortcut || !body) {
      toast.error('Shortcut and reply text are required'); return;
    }
    setSaving(true);
    try {
      if (cannedModal?.crId) {
        await inboxUpdateCanned(bId, { crId: cannedModal.crId, shortcut, body });
        toast.success('Canned response updated');
      } else {
        await inboxCreateCanned(bId, { shortcut, body });
        toast.success('Canned response created');
      }
      setCannedModal(null);
      setForm({ shortcut: '', body: '' });
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save canned response');
    }
    setSaving(false);
  }

  async function doDelete(c) {
    try {
      await inboxDeleteCanned(bId, c.crId);
      toast.success('Canned response deleted');
      onReload();
    } catch { toast.error('Failed to delete'); }
  }

  return (
    <div className="agents-panel">
      <div className="agents-panel-header">
        <span className="agents-panel-header-title">Canned Responses ({cannedResponses.length})</span>
        <div className="agents-panel-header-actions">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => { setForm({ shortcut: '', body: '' }); setCannedModal('create'); }}
          >
            <i className="fas fa-plus" /> Add Shortcut
          </button>
        </div>
      </div>

      <div className="ibx-panel-hint">
        Type <strong>/</strong> followed by a shortcut in the reply box (e.g. <strong>/pricing</strong>)
        to instantly insert the saved reply below.
      </div>

      {cannedResponses.length === 0 ? (
        <div className="inbox-empty">No canned responses yet. Add one above.</div>
      ) : (
        <ul className="agent-list">
          {cannedResponses.map(c => (
            <li key={c.crId} className="agent-item">
              <div className="agent-avatar ibx-av-canned"><i className="fa-solid fa-bolt" /></div>
              <div className="agent-meta">
                <div className="agent-name">/{c.crShortcut}</div>
                <div className="agent-email">{c.crBody}</div>
              </div>
              <div className="agent-actions">
                <button className="icon-btn" onClick={() => {
                  setForm({ shortcut: c.crShortcut, body: c.crBody });
                  setCannedModal(c);
                }} aria-label="Edit"><i className="fa-solid fa-pen" /></button>
                <button className="icon-btn danger" onClick={() => setConfirmDelete(c)} aria-label="Delete"><i className="fa-regular fa-trash-can" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Canned response modal */}
      {cannedModal && (
        <div className="modal-overlay" onClick={() => setCannedModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{cannedModal?.crId ? 'Edit Canned Response' : 'Add Canned Response'}</h3>
              <button className="icon-btn" onClick={() => setCannedModal(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <label className="field-label">Shortcut</label>
              <input
                className="field-input"
                value={form.shortcut}
                onChange={e => setForm(f => ({ ...f, shortcut: e.target.value }))}
                placeholder="pricing"
              />
              <label className="field-label">Reply text</label>
              <textarea
                className="field-input"
                rows={5}
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Our plans start at $9/month…"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setCannedModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete canned response?"
          message={`"/${confirmDelete.crShortcut}" will no longer be available in the reply autocomplete.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => { doDelete(confirmDelete); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ── Labels management panel ─────────────────────────────────────
const LABEL_PALETTE = ['#6366f1','#ef4444','#f59e0b','#10b981','#0ea5a4','#3b82f6','#a855f7','#ec4899','#84cc16','#64748b'];

function LabelsPanel({ bId, labels, onReload, labelModal, setLabelModal }) {
  const [form, setForm]         = useState({ name: '', color: LABEL_PALETTE[0] });
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | label obj

  async function handleSave() {
    const name = form.name.trim();
    if (!name) { toast.error('Label name is required'); return; }
    setSaving(true);
    try {
      if (labelModal?.lId) {
        await inboxUpdateLabel(bId, { lId: labelModal.lId, name, color: form.color });
        toast.success('Label updated');
      } else {
        await inboxCreateLabel(bId, { name, color: form.color });
        toast.success('Label created');
      }
      setLabelModal(null);
      setForm({ name: '', color: LABEL_PALETTE[0] });
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save label');
    }
    setSaving(false);
  }

  async function doDelete(l) {
    try {
      await inboxDeleteLabel(bId, l.lId);
      toast.success('Label deleted');
      onReload();
    } catch { toast.error('Failed to delete'); }
  }

  return (
    <div className="agents-panel">
      <div className="agents-panel-header">
        <span className="agents-panel-header-title">Labels ({labels.length})</span>
        <div className="agents-panel-header-actions">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => { setForm({ name: '', color: LABEL_PALETTE[0] }); setLabelModal('create'); }}
          >
            <i className="fas fa-plus" /> Add Label
          </button>
        </div>
      </div>

      <div className="ibx-panel-hint">
        Labels let you triage and filter conversations at a glance — apply them from any open chat's header.
      </div>

      {labels.length === 0 ? (
        <div className="inbox-empty">No labels yet. Add one above.</div>
      ) : (
        <ul className="agent-list">
          {labels.map(l => (
            <li key={l.lId} className="agent-item">
              <div className="agent-avatar" style={{ background: `${l.lColor}1f`, color: l.lColor }}>
                <i className="fa-solid fa-tag" />
              </div>
              <div className="agent-meta">
                <div className="agent-name">{l.lName}</div>
              </div>
              <div className="agent-actions">
                <button className="icon-btn" onClick={() => {
                  setForm({ name: l.lName, color: l.lColor });
                  setLabelModal(l);
                }} aria-label="Edit"><i className="fa-solid fa-pen" /></button>
                <button className="icon-btn danger" onClick={() => setConfirmDelete(l)} aria-label="Delete"><i className="fa-regular fa-trash-can" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Label modal */}
      {labelModal && (
        <div className="modal-overlay" onClick={() => setLabelModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{labelModal?.lId ? 'Edit Label' : 'Add Label'}</h3>
              <button className="icon-btn" onClick={() => setLabelModal(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <label className="field-label">Name</label>
              <input
                className="field-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="billing, urgent, vip…"
              />
              <label className="field-label">Color</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {LABEL_PALETTE.map(c => (
                  <ColorSwatch
                    key={c}
                    color={c}
                    active={form.color === c}
                    onClick={() => setForm(f => ({ ...f, color: c }))}
                  />
                ))}
              </div>
              <div style={{ marginTop: 2 }}>
                <LabelChip label={{ lName: form.name.trim() || 'Preview', lColor: form.color }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setLabelModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete label?"
          message={`"${confirmDelete.lName}" will be removed from every conversation it's applied to.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => { doDelete(confirmDelete); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}


// ── Reports panel — SLA / response-time reporting ────────────────
function formatDuration(secs) {
  if (secs === null || secs === undefined) return '—';
  secs = Math.round(secs);
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return remMins ? `${hrs}h ${remMins}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs ? `${days}d ${remHrs}h` : `${days}d`;
}

function ReportsPanel({ data, loading, range, onRangeChange }) {
  const overview = data?.overview;
  const perAgent = data?.perAgent ?? [];
  const RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'week',  label: 'This week' },
    { key: 'all',   label: 'All time' },
  ];

  return (
    <div className="agents-panel">
      <div className="ibx-panel-head">
        <div className="ibx-seg">
          {RANGES.map(r => (
            <button
              key={r.key}
              type="button"
              className={`ibx-seg-btn ${range === r.key ? 'is-active' : ''}`}
              onClick={() => onRangeChange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="inbox-empty">Loading…</div>
      ) : !overview ? (
        <div className="inbox-empty">No report data yet.</div>
      ) : (
        <div className="ibx-reports">
          <div className="ibx-stat-grid">
            <div className="ibx-stat">
              <span><i className="fa-solid fa-stopwatch" /> First response</span>
              <b>{formatDuration(overview.avgFirstResponseSecs)}</b>
            </div>
            <div className="ibx-stat">
              <span><i className="fa-solid fa-flag-checkered" /> Resolution</span>
              <b>{formatDuration(overview.avgResolutionSecs)}</b>
            </div>
            <div className="ibx-stat ibx-stat-wide">
              <span><i className="fa-solid fa-circle-check" /> Conversations resolved</span>
              <b>{overview.resolvedCount}</b>
            </div>
          </div>

          <div className="ibx-section-label">By agent</div>
          {perAgent.length === 0 ? (
            <div className="ibx-reports-empty">No resolved conversations with an agent in this range yet.</div>
          ) : (
            <div className="ibx-agent-stats">
              {perAgent.map(a => (
                <div key={a.agentId} className="ibx-agent-stat">
                  <div className="ibx-agent-stat-top">
                    <span className="ibx-mini-av">{(a.agentName || '?')[0].toUpperCase()}</span>
                    <b>{a.agentName || 'Unknown agent'}</b>
                    <em>{a.resolvedCount} resolved</em>
                  </div>
                  <div className="ibx-agent-stat-row">
                    <div><span>Response</span><b>{formatDuration(a.avgFirstResponseSecs)}</b></div>
                    <div><span>Resolution</span><b>{formatDuration(a.avgResolutionSecs)}</b></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}